import { useEffect, useRef, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import MapGL, { Marker, NavigationControl, Popup, type MapRef } from 'react-map-gl/maplibre';
import 'maplibre-gl/dist/maplibre-gl.css';
import { useQuery } from '@tanstack/react-query';

import { api } from '@/lib/api';
import { requirePermission } from '@/lib/route-guards';
import { getSocket } from '@/lib/socket';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { QueryError } from '@/components/QueryError';

import { relativeTime, resolveMapStyle } from '@logistech/ui-shared';
import type { Vehicle, VehicleState } from '@logistech/ui-shared';

const MAP_STYLE = resolveMapStyle(import.meta.env.VITE_MAP_STYLE);

export const Route = createFileRoute('/_authed/map')({
  beforeLoad: () => requirePermission('vehicles.read'),
  component: MapPage,
});

const MOVEMENT_LABELS: Record<VehicleState['movement'], string> = {
  MOVING: 'En mouvement', STOPPED: 'À l\u2019arrêt', IDLE: 'Moteur au ralenti', OFFLINE: 'Hors ligne',
};

interface VehicleWithState extends Vehicle {
  state?: VehicleState;
}

interface PositionEvent {
  vehicleId: string;
  lat: number;
  lon: number;
  speedKmh: number;
  heading: number;
  time: string;
}

function MapPage() {
  const mapRef = useRef<MapRef | null>(null);
  const [selected, setSelected] = useState<VehicleWithState | null>(null);
  const [vehicles, setVehicles] = useState<globalThis.Map<string, VehicleWithState>>(new globalThis.Map());

  const query = useQuery({
    queryKey: ['fleet-map'],
    queryFn: async () => {
      const [vehRes, statesRes] = await Promise.all([
        api.get<{ data: Vehicle[] }>('/vehicles?pageSize=100'),
        api.get<{ data: VehicleState[] }>('/positions/states'),
      ]);
      const stateMap = new globalThis.Map((statesRes.data?.data ?? []).map((s) => [s.vehicleId, s]));
      const merged = new globalThis.Map<string, VehicleWithState>();
      for (const v of (vehRes.data?.data ?? [])) merged.set(v.id, { ...v, state: stateMap.get(v.id) });
      setVehicles(merged);
      return vehRes.data;
    },
    refetchInterval: 60_000,
  });

  useEffect(() => {
    const s = getSocket();
    const handler = (msg: PositionEvent) => {
      setVehicles((prev) => {
        const next = new globalThis.Map(prev);
        const v = next.get(msg.vehicleId);
        if (!v) return prev;
        next.set(msg.vehicleId, {
          ...v,
          state: {
            ...(v.state ?? {
              vehicleId: msg.vehicleId,
              ignition: null,
              fuelLevelL: null,
              online: true,
              movement: 'MOVING' as const,
              lastSpeedKmh: null,
              lastHeading: null,
              lastLat: null,
              lastLon: null,
              lastTime: null,
            }),
            lastLat: msg.lat,
            lastLon: msg.lon,
            lastSpeedKmh: msg.speedKmh,
            lastHeading: msg.heading,
            lastTime: msg.time,
            online: true,
            movement: msg.speedKmh > 5 ? 'MOVING' : 'STOPPED',
          },
        });
        return next;
      });
    };
    s.on('position', handler);
    s.emit('subscribe:tenant');
    return () => { s.off('position', handler); };
  }, []);

  const vehList = Array.from(vehicles.values()).filter((v) => v.state?.lastLat && v.state?.lastLon);

  // Centrage sur la flotte au premier chargement (la vue par défaut couvre la Guinée)
  const fitted = useRef(false);
  useEffect(() => {
    if (fitted.current || vehList.length === 0 || !mapRef.current) return;
    fitted.current = true;
    const lons = vehList.map((v) => v.state!.lastLon!);
    const lats = vehList.map((v) => v.state!.lastLat!);
    if (vehList.length === 1) {
      mapRef.current.flyTo({ center: [lons[0]!, lats[0]!], zoom: 13 });
    } else {
      mapRef.current.fitBounds(
        [[Math.min(...lons), Math.min(...lats)], [Math.max(...lons), Math.max(...lats)]],
        { padding: 80, maxZoom: 14 },
      );
    }
  }, [vehList]);

  return (
    <div className="h-[calc(100vh-3.5rem)] flex">{/* 3.5rem = TopBar */}
      <aside className="w-72 overflow-y-auto bg-card border-r p-3 space-y-2">
        <h2 className="font-semibold text-sm mb-2">Véhicules ({vehList.length})</h2>
        <QueryError query={query} className="flex-wrap" />
        {vehList.map((v) => (
          <button
            key={v.id}
            onClick={() => {
              setSelected(v);
              if (v.state?.lastLon && v.state.lastLat) {
                mapRef.current?.flyTo({ center: [v.state.lastLon, v.state.lastLat], zoom: 14 });
              }
            }}
            className="w-full text-left p-2 rounded hover:bg-muted"
          >
            <div className="flex items-center justify-between">
              <span className="font-medium text-sm">{v.plate}</span>
              <Badge variant={v.state?.movement === 'MOVING' ? 'default' : 'secondary'}>
                {MOVEMENT_LABELS[v.state?.movement ?? 'OFFLINE']}
              </Badge>
            </div>
            <div className="text-xs text-muted-foreground">
              {v.state?.online
                ? `${v.state.lastSpeedKmh?.toFixed(0) ?? '—'} km/h`
                : `Dernier signal ${v.state?.lastTime ? relativeTime(v.state.lastTime) : '—'}`}
            </div>
          </button>
        ))}
      </aside>

      <div className="flex-1">
        <MapGL
          ref={mapRef}
          mapStyle={MAP_STYLE}
          initialViewState={{ longitude: -13.7, latitude: 9.6, zoom: 6 }}
          attributionControl
        >
          <NavigationControl position="top-right" />
          {vehList.map(
            (v) =>
              v.state?.lastLat &&
              v.state.lastLon && (
                <Marker
                  key={v.id}
                  longitude={v.state.lastLon}
                  latitude={v.state.lastLat}
                  anchor="bottom"
                  onClick={(e) => {
                    e.originalEvent.stopPropagation();
                    setSelected(v);
                  }}
                >
                  <div
                    className={`text-white text-xs px-2 py-1 rounded-full shadow cursor-pointer ${
                      v.state?.online ? 'bg-primary-500 hover:bg-primary-700' : 'bg-gray-400 hover:bg-gray-500'
                    }`}
                  >
                    {v.plate}
                  </div>
                </Marker>
              ),
          )}
          {selected?.state?.lastLat && selected.state.lastLon && (
            <Popup
              longitude={selected.state.lastLon}
              latitude={selected.state.lastLat}
              anchor="top"
              onClose={() => setSelected(null)}
              closeOnClick={false}
            >
              <Card className="border-0 shadow-none min-w-[160px]">
                <CardContent className="p-3 space-y-1 text-sm">
                  <div className="font-bold">{selected.plate}</div>
                  <div className="text-muted-foreground">
                    {selected.make} {selected.model}
                  </div>
                  <div>{MOVEMENT_LABELS[selected.state.movement]}</div>
                  <div>Vitesse : {selected.state.lastSpeedKmh?.toFixed(0) ?? '—'} km/h</div>
                  {selected.state.lastTime && <div>Dernier signal : {relativeTime(selected.state.lastTime)}</div>}
                  <div>Odo : {selected.odometerKm.toLocaleString('fr-FR')} km</div>
                  {selected.state.fuelLevelL != null && (
                    <div>Réservoir : {selected.state.fuelLevelL.toFixed(0)} L</div>
                  )}
                </CardContent>
              </Card>
            </Popup>
          )}
        </MapGL>
      </div>
    </div>
  );
}
